import { FC } from 'react';
import { IoClose } from 'react-icons/io5';

import { ActionIcon, Image, ImageProps, Modal } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { File } from 'models';
import { ImageKitHelpers } from 'helpers/ImageKitHelpers';

import { useFile } from 'hooks/use-file';

import cx from './IKImage.module.scss';

// TODO: add support for sizes?
export type IKImageProps = Omit<ImageProps, 'src'> & {
    width: number;
    height?: number;
    alt?: string;
    disableCrop?: boolean;
    fileOrId: File | File['id'];
    loading?: 'lazy' | 'eager';
    fetchPriority?: 'high' | 'low';
    blurProps?: {
        enabled?: boolean;
        belowDimensions?: { width?: number; height?: number } | null;
        radius?: number;
    };
    fallbackUrl?: string;
    enableZoomModal?: boolean;
};

const IKImage: FC<IKImageProps> = ({
    fileOrId,
    width,
    height,
    disableCrop,
    loading = 'lazy',
    fetchPriority = 'low',
    blurProps = {
        enabled: false,
    },
    fallbackUrl,
    enableZoomModal,
    ...props
}) => {
    const blurDimensions = blurProps?.enabled
        ? {
              width: blurProps?.belowDimensions?.width ?? width ?? 0,
              height: blurProps?.belowDimensions?.height ?? height ?? 0,
          }
        : null;

    const blurRadius = blurProps?.radius ?? 50;

    const { file } = useFile(fileOrId);
    const [opened, { open, close }] = useDisclosure(false);

    const dimensions = {
        width: file?.width ?? width,
        height: file?.height ?? height,
    };

    if (width && height) {
        const ratio = width / height;

        dimensions.width = width;
        dimensions.height = dimensions.width / ratio;
    }

    const doBlur =
        blurDimensions && file?.width && file?.height
            ? file.width < blurDimensions.width || file.height < blurDimensions.height
            : false;

    const src = file
        ? ImageKitHelpers.getSrcFromFile(file, {
              width,
              height,
              disableCrop,
              dpr: 2,
              blur: doBlur ? blurRadius : undefined,
          })
        : null;

    const imageComponent = file ? (
        <Image
            src={src}
            width={dimensions.width}
            height={dimensions.height}
            className={`${cx.image} ${props.className}`}
            loading={loading}
            decoding="async"
            fetchPriority={fetchPriority}
            onClick={enableZoomModal ? open : undefined}
            style={{ cursor: enableZoomModal ? 'zoom-in' : undefined }}
            onError={(event) => {
                if (fallbackUrl) {
                    event.currentTarget.src = fallbackUrl;
                }
            }}
            {...props}
        />
    ) : (
        <Image
            src={null}
            width={dimensions.width}
            height={dimensions.height}
            className={`${cx.image} ${props.className}`}
            opacity={0} // hide broken image icon
            {...props}
        />
    );

    return (
        <>
            {imageComponent}
            {enableZoomModal && file && (
                <Modal
                    centered
                    opened={opened}
                    onClose={close}
                    withCloseButton={false}
                    size="80vw"
                    classNames={{ body: cx.modalBody }}
                >
                    <ActionIcon variant="white" size="lg" radius="xl" onClick={close} className={cx.closeButton}>
                        <IoClose size={20} />
                    </ActionIcon>
                    <IKImage fileOrId={file} width={1200} alt={props.alt} />
                </Modal>
            )}
        </>
    );
};

export { IKImage };
